import { api } from "@/lib/api"
import { useAppStore } from "@/store"
import type { Comment } from "@/types/const.types"
import { scrollToComment } from "@/utils/scroll-to-comment"
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@preview-kit/ui/components/dropdown-menu"
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@preview-kit/ui/components/tooltip"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import {
	CircleCheck,
	Copy,
	Edit,
	MessageCircle,
	MoreVertical,
	Trash2,
} from "lucide-react"

dayjs.extend(relativeTime)

interface CommentItemProps {
	comment: Comment
	author: string
	content: string
	timestamp: string
	avatar: string
	repliesCount?: number
	repliesAvatars?: string[]
}

const formatTimestamp = (timestamp: string): string => {
	// Parse the timestamp - handle ISO strings, Date objects converted to strings, etc.
	const date = dayjs(timestamp)

	// Validate that we have a valid date
	if (!date.isValid()) {
		return "Invalid date"
	}

	const now = dayjs()
	const diffInMinutes = now.diff(date, "minute")
	const diffInHours = now.diff(date, "hour")
	const diffInDays = now.diff(date, "day")

	// Less than 1 minute ago
	if (diffInMinutes < 1) {
		return "just now"
	}

	// Less than 1 hour ago
	if (diffInHours < 1) {
		return `${diffInMinutes}m ago`
	}

	// Less than 24 hours ago
	if (diffInDays < 1) {
		return `${diffInHours}h ago`
	}

	// Less than 7 days ago
	if (diffInDays < 7) {
		return `${diffInDays}d ago`
	}

	// More than 7 days ago, show actual date
	if (diffInDays < 365) {
		return date.format("MMM D")
	}

	// More than 1 year ago, show year as well
	return date.format("MMM D, YYYY")
}

export default function CommentItem({
	comment,
	author,
	content,
	timestamp,
	avatar,
	repliesCount = 0,
	repliesAvatars = [],
}: CommentItemProps) {
	const { setSelectedComment, setCommentsOpen } = useAppStore()
	const queryClient = useQueryClient()

	// Mutation for updating comment
	const updateCommentMutation = useMutation({
		mutationFn: async ({
			commentId,
			resolved,
		}: {
			commentId: string
			resolved: boolean
		}) =>
			await api
				.patch(`comments/${commentId}`, { json: { resolved } })
				.json<Comment>(),
		onMutate: async ({ commentId, resolved }) => {
			// Cancel any outgoing refetches
			await queryClient.cancelQueries({ queryKey: ["comments"] })

			// Snapshot the previous value
			const previousComments = queryClient.getQueryData<Comment[]>(["comments"])

			// Optimistically update to the new value
			queryClient.setQueryData<Comment[]>(["comments"], (old) => {
				if (!old) return []
				return old.map((comment) =>
					comment.id === commentId ? { ...comment, resolved } : comment,
				)
			})

			// Return a context object with the snapshotted value
			return { previousComments }
		},
		onError: (_err, _variables, context) => {
			// If the mutation fails, use the context returned from onMutate to roll back
			queryClient.setQueryData(["comments"], context?.previousComments)
		},
		onSettled: () => {
			// Always refetch after error or success to ensure we're in sync with server
			queryClient.invalidateQueries({ queryKey: ["comments"] })
		},
	})

	// Mutation for deleting comment
	const deleteCommentMutation = useMutation({
		mutationFn: async (commentId: string) =>
			await api.delete(`comments/${commentId}`).json(),
		onSuccess: () => {
			// Refetch comments after successful deletion
			queryClient.invalidateQueries({ queryKey: ["comments"] })
		},
		onError: (error) => {
			console.error("Failed to delete comment:", error)
		},
	})

	const handleCommentClick = async () => {
		// Close the comments panel
		setCommentsOpen(false)

		// Wait for panel to close, then scroll to comment and select it
		setTimeout(async () => {
			await scrollToComment(comment)
			// Select the comment after scrolling completes
			setSelectedComment(comment)
		}, 50)
	}

	const handleResolvedToggle = async (e: React.MouseEvent) => {
		e.stopPropagation()
		if (!comment.id) return

		try {
			await updateCommentMutation.mutateAsync({
				commentId: comment.id,
				resolved: !comment.resolved,
			})
		} catch (error) {
			console.error("Failed to update comment:", error)
		}
	}

	const handleDelete = async (e: React.MouseEvent) => {
		e.stopPropagation()
		if (!comment.id) return

		try {
			await deleteCommentMutation.mutateAsync(comment.id)
		} catch (error) {
			console.error("Failed to delete comment:", error)
		}
	}
	return (
		<div
			className="flex p-3 my-2 rounded-lg bg-white dark:bg-[#121212] border border-gray-200 dark:border-[#333] hover:bg-gray-50 dark:hover:bg-[#212121] cursor-pointer transition-colors duration-200"
			onClick={handleCommentClick}
			role="button"
			tabIndex={0}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault()
					handleCommentClick()
				}
			}}
			aria-label={`Go to comment by ${author}: ${content.slice(0, 50)}${content.length > 50 ? "..." : ""}`}
		>
			<img
				src={avatar}
				alt={`${author}'s avatar`}
				className="w-8 h-8 rounded-full flex-shrink-0"
			/>
			<div className="flex-1 min-w-0 ml-3">
				<div className="flex items-center gap-2 mb-1">
					<span className="text-sm font-medium text-gray-900 dark:text-white">
						{author}
					</span>
					<span className="text-xs text-gray-500 dark:text-[#999]">
						{formatTimestamp(timestamp)}
					</span>
				</div>
				<p className="text-sm text-gray-700 dark:text-[#ccc] leading-relaxed break-words overflow-hidden">
					{content}
				</p>
				{repliesCount > 0 && (
					<div className="flex items-center gap-2 mt-2">
						<div className="flex items-center gap-1">
							<MessageCircle className="w-3 h-3 text-gray-500 dark:text-[#999]" />
							<span className="text-xs text-gray-500 dark:text-[#999]">
								{repliesCount} {repliesCount === 1 ? "reply" : "replies"}
							</span>
						</div>
						{repliesAvatars.length > 0 && (
							<div className="flex -space-x-1">
								{repliesAvatars.slice(0, 3).map((replyAvatar, index) => (
									<img
										key={index}
										src={replyAvatar}
										alt={`Reply ${index + 1} avatar`}
										className="w-6 h-6 rounded-full border border-gray-300 dark:border-[#333] bg-gray-100 dark:bg-[#252525]"
									/>
								))}
								{repliesAvatars.length > 3 && (
									<div className="w-6 h-6 rounded-full border border-gray-300 dark:border-[#333] bg-gray-100 dark:bg-[#252525] flex items-center justify-center">
										<span className="text-[9px] text-gray-600 dark:text-[#999] font-medium">
											+{repliesAvatars.length - 3}
										</span>
									</div>
								)}
							</div>
						)}
					</div>
				)}
			</div>
			<Tooltip>
				<TooltipTrigger asChild>
					<button
						className="flex mr-auto items-center justify-center w-6 h-6 cursor-pointer"
						onClick={handleResolvedToggle}
					>
						<CircleCheck
							className={`w-4 h-4 transition-all duration-175 ${
								comment.resolved
									? "text-green-500 dark:text-green-400"
									: "text-gray-400 dark:text-[#666] hover:text-green-500 dark:hover:text-green-300"
							}`}
						/>
					</button>
				</TooltipTrigger>
				<TooltipContent container={window.__PREVIEWKIT_PORTAL_CONTAINER__}>
					<p>{comment.resolved ? "Mark as unresolved" : "Mark as resolved"}</p>
				</TooltipContent>
			</Tooltip>

			<DropdownMenu modal={false}>
				<DropdownMenuTrigger asChild>
					<button
						className="flex items-center justify-center w-6 h-6 rounded hover:bg-gray-100 dark:hover:bg-[#333] text-gray-400 dark:text-[#666] hover:text-gray-600 dark:hover:text-white transition-colors cursor-pointer"
						onClick={(e) => e.stopPropagation()}
					>
						<MoreVertical className="w-4 h-4" />
					</button>
				</DropdownMenuTrigger>
				<DropdownMenuContent
					align="end"
					className="w-40"
					container={window.__PREVIEWKIT_PORTAL_CONTAINER__}
				>
					<DropdownMenuItem className="cursor-pointer">
						<Copy className="w-4 h-4 mr-2" />
						Copy Link
					</DropdownMenuItem>
					<DropdownMenuItem className="cursor-pointer">
						<Edit className="w-4 h-4 mr-2" />
						Edit
					</DropdownMenuItem>
					<DropdownMenuSeparator />
					<DropdownMenuItem
						className="cursor-pointer text-red-600 dark:text-red-400 focus:text-red-600 dark:focus:text-red-400"
						variant="destructive"
						onClick={handleDelete}
					>
						<Trash2 className="w-4 h-4 mr-2" />
						Delete
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	)
}
